import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  Heading,
  Card,
  CardBody,
  Container,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftElement,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  Divider,
  Center,
  Icon,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaKey, FaUserMd, FaVials, FaFlask, FaUser, FaBuilding } from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import { checkLockout, recordFailedAttempt, clearAttempts } from '../utils/loginRateLimiter';

const UnifiedLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState(null); // 'patient', 'phlebotomist', 'lab', 'admin'
  const [detectedUserType, setDetectedUserType] = useState(null);
  const [detectedUserInfo, setDetectedUserInfo] = useState(null);
  
  const toast = useToast();
  const navigate = useNavigate();

  // Detect user type based on email or input
  const detectUserType = async (emailInput) => {
    if (!emailInput) {
      setDetectedUserType(null);
      setDetectedUserInfo(null);
      return;
    }

    // Check if it's a lab secret key format (LAB-XXXX-XXXX-XXXX)
    if (emailInput.match(/^LAB-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/)) {
      setDetectedUserType('lab');
      setSecretKey(emailInput);
      setEmail('');
      return;
    }

    // Check all tables in parallel — use allSettled so one failing table doesn't break all detection
    try {
      const results = await Promise.allSettled([
        supabase.from('admin_users').select('id').eq('email', emailInput).maybeSingle(),
        supabase.from('phlebotomist_profiles').select('id').eq('email', emailInput).maybeSingle(),
        supabase.from('facilities').select('id').eq('contact_email', emailInput).maybeSingle(),
        supabase.from('labs').select('id').eq('contact_email', emailInput).maybeSingle(),
      ]);

      const adminRes = results[0].status === 'fulfilled' ? results[0].value : {};
      const phlebRes = results[1].status === 'fulfilled' ? results[1].value : {};
      const facilityRes = results[2].status === 'fulfilled' ? results[2].value : {};
      const labRes = results[3].status === 'fulfilled' ? results[3].value : {};

      if (adminRes.data) { setDetectedUserType('admin'); setDetectedUserInfo(null); return; }
      if (phlebRes.data) {
        setDetectedUserType('phlebotomist');
        // Fetch the phleb's name so the login screen can confirm visually
        // ("Welcome back, Jane") before they even type a password.
        const { data: full } = await supabase
          .from('phlebotomist_profiles')
          .select('full_name, company_name')
          .eq('id', phlebRes.data.id)
          .maybeSingle();
        setDetectedUserInfo(full || null);
        return;
      }
      if (facilityRes.data) { setDetectedUserType('facility'); setDetectedUserInfo(null); return; }
      if (labRes.data) { setDetectedUserType('lab'); setDetectedUserInfo(null); return; }
    } catch (error) {
      // Fall through to default
    }

    // Default to patient if no specific type detected
    setDetectedUserType('patient');
    setDetectedUserInfo(null);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    detectUserType(value);
  };

  const handleLabLogin = async () => {
    const lockout = checkLockout('unified_lab');
    if (lockout.locked) {
      toast({ title: 'Account Locked', description: `Too many failed attempts. Try again in ${lockout.remainingMinutes} minutes.`, status: 'error', duration: 5000 });
      return;
    }
    try {
      setLoading(true);

      // Validate secret key with labs table
      const { data, error } = await supabase
        .from('labs')
        .select('*')
        .eq('secret_key', secretKey)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        const result = recordFailedAttempt('unified_lab');
        throw new Error(result.locked
          ? `Too many failed attempts. Try again in ${result.remainingMinutes} minutes.`
          : `Invalid secret key or inactive lab. ${result.attemptsRemaining} attempts remaining.`);
      }

      clearAttempts('unified_lab');
      // Store lab session — never persist secret_key
      const labSession = {
        id: data.id,
        name: data.name,
        logo_url: data.logo_url,
        is_main_key: true,
        loginTime: new Date().toISOString(),
      };

      localStorage.setItem('labSession', JSON.stringify(labSession));

      toast({
        title: 'Login Successful',
        description: `Welcome to ${data.name} portal`,
        status: 'success',
        duration: 3000,
      });

      navigate('/lab-portal/dashboard');
    } catch (error) {
      toast({
        title: 'Lab Login Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async () => {
    const lockout = checkLockout('unified_admin');
    if (lockout.locked) {
      toast({ title: 'Account Locked', description: `Too many failed attempts. Try again in ${lockout.remainingMinutes} minutes.`, status: 'error', duration: 5000 });
      return;
    }
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('email', email)
        .eq('password', password)
        .single();

      if (error || !data) {
        const result = recordFailedAttempt('unified_admin');
        throw new Error(result.locked
          ? `Too many failed attempts. Try again in ${result.remainingMinutes} minutes.`
          : `Invalid admin credentials. ${result.attemptsRemaining} attempts remaining.`);
      }

      clearAttempts('unified_admin');
      // Store only non-sensitive fields — never persist password or secret_code
      const { password: _pw, secret_code: _sc, ...safeAdminData } = data;
      localStorage.setItem('adminUser', JSON.stringify(safeAdminData));
      // Establish the Dialerr agent session cookie (best-effort — never blocks login).
      try {
        await fetch('/api/dialerr/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ email, password }),
        });
      } catch (e) { /* softphone sign-in is optional */ }

      toast({
        title: 'Admin Login Successful',
        description: 'Welcome to the admin panel',
        status: 'success',
        duration: 3000,
      });

      navigate('/admin');
    } catch (error) {
      toast({
        title: 'Admin Login Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePhlebotomistLogin = async () => {
    const lockout = checkLockout('unified_phleb');
    if (lockout.locked) {
      toast({ title: 'Account Locked', description: `Too many failed attempts. Try again in ${lockout.remainingMinutes} minutes.`, status: 'error', duration: 5000 });
      return;
    }
    try {
      setLoading(true);

      // Phlebs authenticate through Supabase Auth — the admin provisioned the
      // auth.users row when creating the phleb, and the onboarding wizard
      // triggers a password-reset email for them to set their password.
      const { data: authData, error: authErr } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authErr || !authData?.user) {
        const result = recordFailedAttempt('unified_phleb');
        throw new Error(result.locked
          ? `Too many failed attempts. Try again in ${result.remainingMinutes} minutes.`
          : `Invalid phlebotomist credentials. ${result.attemptsRemaining} attempts remaining.`);
      }

      // Pull the phleb profile so we can route to their dashboard URL.
      const { data: phleb, error: phlebErr } = await supabase
        .from('phlebotomist_profiles')
        .select('id, full_name, email, company_name, archived')
        .eq('id', authData.user.id)
        .maybeSingle();

      if (phlebErr || !phleb) {
        await supabase.auth.signOut();
        throw new Error('This email is not registered as a phlebotomist.');
      }
      if (phleb.archived) {
        await supabase.auth.signOut();
        throw new Error('This phlebotomist account is archived. Please contact support.');
      }

      clearAttempts('unified_phleb');
      localStorage.setItem('phlebotomistUser', JSON.stringify(phleb));

      toast({
        title: 'Login Successful',
        description: `Welcome back, ${phleb.full_name || phleb.email}`,
        status: 'success',
        duration: 3000,
      });

      navigate(`/phlebotomist/${phleb.id}`);
    } catch (error) {
      toast({
        title: 'Phlebotomist Login Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFacilityLogin = async () => {
    const lockout = checkLockout('unified_facility');
    if (lockout.locked) {
      toast({ title: 'Account Locked', description: `Too many failed attempts. Try again in ${lockout.remainingMinutes} minutes.`, status: 'error', duration: 5000 });
      return;
    }
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('facilities')
        .select('id, name, contact_email, contact_name, contact_phone, facility_type, address, city, state, zip, agreement_signed, is_active, password')
        .eq('contact_email', email)
        .eq('password', password)
        .single();

      if (error || !data) {
        const result = recordFailedAttempt('unified_facility');
        throw new Error(result.locked
          ? `Too many failed attempts. Try again in ${result.remainingMinutes} minutes.`
          : `Invalid facility credentials. ${result.attemptsRemaining} attempts remaining.`);
      }

      if (!data.is_active) throw new Error('This facility account is inactive. Please contact support.');

      clearAttempts('unified_facility');
      const { password: _pw3, ...safeFacilityData } = data;
      localStorage.setItem('facilityUser', JSON.stringify(safeFacilityData));

      toast({ title: 'Login Successful', description: `Welcome, ${data.name}`, status: 'success', duration: 3000 });
      navigate(`/facility/${data.id}`);
    } catch (error) {
      toast({ title: 'Facility Login Failed', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handlePatientLogin = async () => {
    try {
      setLoading(true);
      
      // For patients, we'll create a simple session based on email
      // In a real app, you'd want proper authentication
      const patientSession = {
        email: email,
        loginTime: new Date().toISOString(),
        type: 'patient'
      };
      
      localStorage.setItem('patientSession', JSON.stringify(patientSession));

      toast({
        title: 'Patient Login Successful',
        description: 'Welcome to your patient portal',
        status: 'success',
        duration: 3000,
      });

      navigate('/patient/dashboard');
    } catch (error) {
      toast({
        title: 'Patient Login Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (detectedUserType === 'lab' && secretKey) {
      await handleLabLogin();
    } else if (detectedUserType === 'admin') {
      await handleAdminLogin();
    } else if (detectedUserType === 'phlebotomist') {
      await handlePhlebotomistLogin();
    } else if (detectedUserType === 'facility') {
      await handleFacilityLogin();
    } else {
      await handlePatientLogin();
    }
  };

  const getUserTypeIcon = (type) => {
    switch (type) {
      case 'admin': return FaUserMd;
      case 'phlebotomist': return FaVials;
      case 'facility': return FaBuilding;
      case 'lab': return FaFlask;
      case 'patient': return FaUser;
      default: return FaUser;
    }
  };

  const getUserTypeLabel = (type) => {
    switch (type) {
      case 'admin': return 'Admin Portal';
      case 'phlebotomist': return 'Phlebotomist Portal';
      case 'facility': return 'Facility Portal';
      case 'lab': return 'Laboratory Portal';
      case 'patient': return 'Patient Portal';
      default: return 'Login';
    }
  };

  const getUserTypeColor = (type) => {
    switch (type) {
      case 'admin': return 'red';
      case 'phlebotomist': return 'blue';
      case 'facility': return 'teal';
      case 'lab': return 'green';
      case 'patient': return 'purple';
      default: return 'gray';
    }
  };

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="md">
        <Center mb={8}>
          <VStack spacing={4}>
            <Box p={4} bg="blue.500" borderRadius="full">
              <Icon as={FaUser} boxSize={8} color="white" />
            </Box>
            <VStack spacing={2} textAlign="center">
              <Heading size="lg" color="gray.700">
                Universal Login
              </Heading>
              <Text color="gray.600" fontSize="md">
                Enter your credentials to access your portal
              </Text>
            </VStack>
          </VStack>
        </Center>

        {/* User Type Detection Card — when we can confirm the email we show the
            phleb's name so they know they're signing into the right account. */}
        {detectedUserType && (
          <Box
            mb={6}
            borderRadius="lg"
            borderWidth="2px"
            borderColor={`${getUserTypeColor(detectedUserType)}.300`}
            bg={`${getUserTypeColor(detectedUserType)}.50`}
            p={4}
          >
            <HStack spacing={3} align="center">
              <Flex
                align="center"
                justify="center"
                bg={`${getUserTypeColor(detectedUserType)}.500`}
                color="white"
                boxSize="42px"
                borderRadius="full"
              >
                <Icon as={getUserTypeIcon(detectedUserType)} boxSize={5} />
              </Flex>
              <Box flex={1}>
                <Text fontSize="2xs" fontWeight="700" color={`${getUserTypeColor(detectedUserType)}.700`} textTransform="uppercase" letterSpacing="0.08em">
                  Signing in to
                </Text>
                <Text fontSize="md" fontWeight="700" color="gray.800" lineHeight="1.2">
                  {getUserTypeLabel(detectedUserType)}
                </Text>
                {detectedUserType === 'phlebotomist' && detectedUserInfo?.full_name && (
                  <Text fontSize="sm" color="gray.600" mt={0.5}>
                    Welcome back, <b>{detectedUserInfo.full_name}</b>
                    {detectedUserInfo.company_name ? ` · ${detectedUserInfo.company_name}` : ''}
                  </Text>
                )}
              </Box>
            </HStack>
          </Box>
        )}

        <Card>
          <CardBody>
            <form onSubmit={handleLogin}>
              <VStack spacing={6}>
                {/* Email or Secret Key Input */}
                <FormControl isRequired>
                  <FormLabel>
                    {detectedUserType === 'lab' ? 'Lab Secret Key' : 'Email Address'}
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement>
                      <Icon 
                        as={detectedUserType === 'lab' ? FaKey : FaEnvelope} 
                        color="gray.400" 
                      />
                    </InputLeftElement>
                    <Input
                      type={detectedUserType === 'lab' ? 'password' : 'email'}
                      value={detectedUserType === 'lab' ? secretKey : email}
                      onChange={detectedUserType === 'lab' ? 
                        (e) => setSecretKey(e.target.value) : 
                        handleEmailChange
                      }
                      placeholder={
                        detectedUserType === 'lab' ? 
                        'LAB-XXXX-XXXX-XXXX' : 
                        'Enter your email address'
                      }
                      size="lg"
                    />
                  </InputGroup>
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    {detectedUserType === 'lab' ? 
                      'Enter your lab\'s secret key to access the portal' :
                      'We\'ll automatically detect your account type'
                    }
                  </Text>
                </FormControl>

                {/* Password Input - only for non-lab users */}
                {detectedUserType && detectedUserType !== 'lab' && detectedUserType !== 'patient' && (
                  <FormControl isRequired>
                    <HStack justify="space-between" mb={1}>
                      <FormLabel mb={0}>Password</FormLabel>
                      {detectedUserType === 'phlebotomist' && (
                        <Button
                          variant="link"
                          size="xs"
                          colorScheme="blue"
                          isDisabled={!email}
                          onClick={async () => {
                            try {
                              const resp = await fetch('/api/send-phleb-password-reset', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ email }),
                              });
                              const body = await resp.json().catch(() => ({}));
                              if (resp.status === 429) {
                                toast({ title: 'Try again in a minute', description: body.error || 'Supabase only allows one password email per 60 seconds.', status: 'warning', duration: 6000, isClosable: true });
                                return;
                              }
                              if (!resp.ok) throw new Error(body.error || `HTTP ${resp.status}`);
                              toast({
                                title: 'Check your email',
                                description: `If ${email} is registered, a password setup link is on its way.`,
                                status: 'success',
                                duration: 6000,
                                isClosable: true,
                              });
                            } catch (e) {
                              toast({ title: "Couldn't send email", description: e.message, status: 'error' });
                            }
                          }}
                        >
                          Forgot password?
                        </Button>
                      )}
                    </HStack>
                    <InputGroup>
                      <InputLeftElement>
                        <Icon as={FaKey} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        size="lg"
                      />
                    </InputGroup>
                  </FormControl>
                )}

                <Button
                  type="submit"
                  colorScheme={getUserTypeColor(detectedUserType)}
                  size="lg"
                  width="100%"
                  isLoading={loading}
                  loadingText="Authenticating..."
                  leftIcon={detectedUserType ? <Icon as={getUserTypeIcon(detectedUserType)} /> : undefined}
                >
                  {detectedUserType ? 
                    `Access ${getUserTypeLabel(detectedUserType)}` : 
                    'Login'
                  }
                </Button>
              </VStack>
            </form>

            <Divider my={6} />

            <VStack spacing={3}>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                Login Examples:
              </Text>
              <VStack spacing={2} fontSize="xs" color="gray.500">
                <HStack>
                  <Icon as={FaFlask} color="green.500" />
                  <Text>Lab: Use secret key (LAB-XXXX-XXXX-XXXX)</Text>
                </HStack>
                <HStack>
                  <Icon as={FaVials} color="blue.500" />
                  <Text>Phlebotomist: phlebotomist@email.com</Text>
                </HStack>
                <HStack>
                  <Icon as={FaUserMd} color="red.500" />
                  <Text>Admin: admin@athomeblooddraw.com</Text>
                </HStack>
                <HStack>
                  <Icon as={FaBuilding} color="teal.500" />
                  <Text>Facility: facility@email.com</Text>
                </HStack>
                <HStack>
                  <Icon as={FaUser} color="purple.500" />
                  <Text>Patient: Any other email</Text>
                </HStack>
              </VStack>
            </VStack>
          </CardBody>
        </Card>

        <Center mt={6}>
          <Text fontSize="sm" color="gray.500">
            Secure access for all user types
          </Text>
        </Center>
      </Container>
    </Box>
  );
};

export default UnifiedLogin;

